import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap, type Schemas } from "@/shared/api/client";
import { unwrapEmpty } from "@/shared/api/empty";
import { makeKeys } from "@/shared/api/queryKeys";
import { useIsLive } from "@/shared/realtime/context";

export type Flow = Schemas["ApprovalFlowOut"];
export type FlowSubject = Schemas["FlowSubject"];
export type FlowStep = Schemas["ApprovalStepOut"];
export type FlowSummary = Schemas["FlowSummaryOut"];
export type Decision = Schemas["RequestDecision"];
export type Delegation = Schemas["DelegationOut"];
export type DelegationCreate = Schemas["DelegationCreate"];
export type ApprovalRoute = Schemas["ApprovalRouteOut"];
export type ApprovalRouteInput = Schemas["ApprovalRouteIn"];
export type RouteSteps = Schemas["RouteSteps"];

export const requestKeys = makeKeys("requests");
export const pendingKey = [...requestKeys.all, "pending"] as const;
const delegationsKey = [...requestKeys.all, "delegations"] as const;
const routesKey = [...requestKeys.all, "routes"] as const;

/* While live updates are off, the inbox and its badge refresh this often (visible tab only). */
const PENDING_POLL_MS = 60_000;

/** Requests whose current step waits for me (as manager, delegate or HR), oldest first. */
export function usePendingRequests() {
  const live = useIsLive();
  return useQuery({
    queryKey: pendingKey,
    queryFn: () => unwrap(api.GET("/api/v1/me/requests/pending")),
    refetchInterval: live ? false : PENDING_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export interface DecisionInput {
  flow: Flow;
  decision: Decision;
  comment: string;
}

/**
 * Send a decision to the module that owns the request: each applies its own effects when it
 * approves (the leave ledger, the attendance day, the payroll claim), so the inbox never decides
 * through a shortcut.
 */
export async function decideFlow({ flow, decision, comment }: DecisionInput): Promise<void> {
  const body = { decision, comment };
  const id = flow.subject_id;
  switch (flow.subject_type) {
    case "attendance_request":
      await unwrap(
        api.POST("/api/v1/attendance-requests/{request_id}/decision", {
          params: { path: { request_id: id } },
          body,
        }),
      );
      return;
    case "leave_request":
      await unwrap(
        api.POST("/api/v1/leave-requests/{request_id}/decision", {
          params: { path: { request_id: id } },
          body,
        }),
      );
      return;
    case "overtime":
      await unwrap(
        api.POST("/api/v1/overtime/{record_id}/decision", {
          params: { path: { record_id: id } },
          body,
        }),
      );
      return;
    case "expense_claim":
      await unwrap(
        api.POST("/api/v1/expense-claims/{claim_id}/decision", {
          params: { path: { claim_id: id } },
          body,
        }),
      );
      return;
    case "letter_request":
      await unwrap(
        api.POST("/api/v1/letters/{request_id}/decision", {
          params: { path: { request_id: id } },
          body,
        }),
      );
      return;
  }
}

/** Approve or reject one request. Pessimistic: it leaves the inbox once the owner accepted. */
export function useDecide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: decideFlow,
    // An approval reaches into the owning module (a leave balance, an attendance day, a
    // payroll claim); refreshing what is on screen keeps every view of it honest.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export interface BulkOutcome {
  approved: Flow[];
  failed: { flow: Flow; error: unknown }[];
}

/** Approve several at once, each through its owner; reports which went through and which did not. */
export function useBulkApprove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flows: readonly Flow[]): Promise<BulkOutcome> => {
      const results = await Promise.allSettled(
        flows.map((flow) => decideFlow({ flow, decision: "approve", comment: "" })),
      );
      const outcome: BulkOutcome = { approved: [], failed: [] };
      results.forEach((result, index) => {
        const flow = flows[index];
        if (!flow) return;
        if (result.status === "fulfilled") outcome.approved.push(flow);
        else outcome.failed.push({ flow, error: result.reason });
      });
      return outcome;
    },
    onSettled: () => queryClient.invalidateQueries(),
  });
}

/* ---- Delegations ---- */

export function useDelegations() {
  return useQuery({
    queryKey: delegationsKey,
    queryFn: () => unwrap(api.GET("/api/v1/me/delegations")),
  });
}

export function useAddDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DelegationCreate) => unwrap(api.POST("/api/v1/me/delegations", { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: delegationsKey }),
  });
}

export function useRemoveDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (delegationId: string) =>
      unwrapEmpty(
        api.DELETE("/api/v1/me/delegations/{delegation_id}", {
          params: { path: { delegation_id: delegationId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: delegationsKey }),
  });
}

/* ---- Approval routes (HR's settings) ---- */

/** Who decides each kind of request, as HR set it (hr.view); kinds not listed go manager then HR. */
export function useApprovalRoutes() {
  return useQuery({
    queryKey: routesKey,
    queryFn: () => unwrap(api.GET("/api/v1/people/settings/approval-routes")),
  });
}

/** Set who decides a subject or one subtype of it (hr.manage). Open requests keep their steps. */
export function useSaveApprovalRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ApprovalRouteInput) =>
      unwrap(api.PUT("/api/v1/people/settings/approval-routes", { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: routesKey }),
  });
}

/** Drop a route: that kind goes back to the default (the manager, then HR). */
export function useResetApprovalRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (routeId: string) =>
      unwrapEmpty(
        api.DELETE("/api/v1/people/settings/approval-routes/{route_id}", {
          params: { path: { route_id: routeId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: routesKey }),
  });
}
